import type { TenantSummary } from "@/services/authService";

/**
 * Search matching for the accountant's cartera (their list of TENANTS — not a
 * tenant's own receptores, which search server-side via listClients).
 *
 * Extracted from accountant-home-page.tsx so it can be tested directly, the
 * same reason nextObligation.ts / dashboardAlertsModel.ts / fiscalHealthScore.ts
 * are plain modules with sibling tests.
 *
 * The version this replaces stripped whitespace from the QUERY but not from the
 * name it compared against:
 *
 *   const needle = query.trim().toLowerCase().replace(/\s+/g, "");
 *   return name.includes(needle) || rut.includes(needle);
 *
 * so `"dogtown media".includes("dogtownmedia")` was false and ANY multi-word
 * query returned nothing. It also never folded accents (typing "gonzalez"
 * missed "González") and compared the RUT with its punctuation intact (typing
 * "452577491" missed "45-2577491"). Search worked only for a single word, or a
 * RUT punctuated exactly as stored — which is why accountants reported they
 * could not find a client.
 */

/** Lowercase and strip diacritics, so "González" and "gonzalez" are equal. */
export function fold(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/**
 * True when every token of the query appears somewhere in the tenant's name or
 * RUT.
 *
 * Tokens are ANDed, not ORed: "dogtown media" must not return every tenant
 * matching either word. Each token matches as a substring rather than a prefix,
 * so "media llc" still finds "DOGTOWN MEDIA LLC".
 */
export function matchesSearch(tenant: TenantSummary, query: string): boolean {
  const tokens = fold(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;

  const rut = tenant.rut ?? "";
  // The RUT goes in twice — as stored, and as bare digits — so a query matches
  // whether or not the accountant types the separator.
  const haystack = `${fold(tenant.name ?? "")} ${fold(rut)} ${rut.replace(/\D/g, "")}`;

  return tokens.every((token) => haystack.includes(token));
}
